import React, { useEffect, useMemo, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@context/auth";
import {
  CREATE_PICKER_REGISTRY,
  CreatePickerKind,
} from "@utils/createPickerRegistry";
import CreateOptionPickerCst from "./index";

let openKind: CreatePickerKind | null = null;
const listeners = new Set<() => void>();

const emit = () => {
  listeners.forEach((l) => l());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => openKind;

export const openCreatePicker = (kind: CreatePickerKind) => {
  if (openKind === kind) return;
  openKind = kind;
  emit();
};

export const closeCreatePicker = () => {
  if (openKind === null) return;
  openKind = null;
  emit();
};

export const useCreatePickerOpenKind = () =>
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

export const canCreate = (
  user: { permissions?: string[] } | null | undefined,
  kind: CreatePickerKind,
): boolean => {
  const required = CREATE_PICKER_REGISTRY[kind].permission;
  const userPermissions = user?.permissions ?? [];
  const requiredList = Array.isArray(required) ? required : [required];
  return requiredList.some((p) => userPermissions.includes(p));
};

export const useCanCreate = (kind: CreatePickerKind): boolean => {
  const { user } = useAuth();
  return canCreate(user, kind);
};

export const CreatePickerHost: React.FC = () => {
  const kind = useCreatePickerOpenKind();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: corporateId = "" } = useParams();
  const { t } = useTranslation("create-picker");

  const allowed = !!kind && canCreate(user, kind);

  useEffect(() => {
    if (kind && !allowed) closeCreatePicker();
  }, [kind, allowed]);

  const def = kind ? CREATE_PICKER_REGISTRY[kind] : null;

  const options = useMemo(() => {
    if (!def) return [];
    const userPermissions = user?.permissions ?? [];
    return (
      def
        .buildOptions({ navigate, corporateId, t })
        // S05: opzioni senza permesso mostrate disabilitate, non nascoste.
        .map((option) => ({
          ...option,
          disabled:
            option.disabled ||
            (!!option.permission &&
              !userPermissions.includes(option.permission)),
          onSelect: () => {
            closeCreatePicker();
            option.onSelect();
          },
        }))
    );
  }, [def, navigate, corporateId, t, user]);

  return (
    <CreateOptionPickerCst
      open={!!kind && allowed}
      onClose={closeCreatePicker}
      title={def ? t(def.titleKey) : ""}
      options={options}
    />
  );
};
